import type { Group, Mesh, MeshBasicMaterial, Object3D, TorusGeometry } from 'three';
import { Vector3 } from 'three';

import type { RotationGizmoManagerConfig } from './types';
import { createGizmoRingsCfn } from './functions/create-gizmo-rings.cfn';
import { disposeCfn } from './functions/dispose.cfn';
import { setupGizmoEventsCfn } from './functions/setup-gizmo-events.cfn';

/**
 * RotationGizmoManager displays and manages an interactive rotation gizmo ring
 * on 3D appliance objects. When activated on an appliance, a single torus ring
 * appears centered on the appliance's bounding box. The ring axis is chosen
 * based on the appliance's placement (userData.subtype):
 * - 'top only': green Y-axis ring (flat on the countertop surface)
 * - all other placements: blue Z-axis ring (parallel to the panel face)
 *
 * Dragging the ring rotates the appliance around its visual center (bounding box
 * center) with 5-degree snap. On drag end, emits a rotation:complete event for
 * the controller to commit to the domain model.
 *
 * @example
 * ```typescript
 * const gizmoManager = new RotationGizmoManager({
 *   scene,
 *   camera,
 *   domElement: renderer.domElement,
 *   emitEvent: (type, data) => engine.emit(type, data),
 *   onInteractionStart: () => { cameraControls.enabled = false; },
 *   onInteractionEnd: () => { cameraControls.enabled = true; }
 * });
 *
 * // Show gizmo on an appliance
 * gizmoManager.activate('appliance-id-123');
 *
 * // Hide gizmo
 * gizmoManager.deactivate();
 *
 * // Cleanup
 * gizmoManager.dispose();
 * ```
 */
export class RotationGizmoManager {
	#config: RotationGizmoManagerConfig;

	// Active state
	#activeObjectId: string | null = null;
	#targetObject: Object3D | null = null;
	#gizmoGroup: Group | null = null;
	#gizmoRings: Array<Mesh> | null = null;

	// Drag state
	#isDragging = false;
	#dragAxis: 'x' | 'y' | 'z' | null = null;
	#dragStartAngle: number | null = null;
	#accumulatedAngle = 0;
	#planeNormal: Vector3 | null = null;
	#planeCenter: Vector3 | null = null;
	#initialTargetRotation: { x: number; y: number; z: number } | null = null;
	#initialTargetPosition: Vector3 | null = null;
	#bboxCenter: Vector3 | null = null;
	#ringNormal: Vector3 | null = null;
	#initialWorldPosition: Vector3 | null = null;
	#worldRotAxis: Vector3 | null = null;
	#justFinishedDrag = false;
	#angleLabelElement: HTMLDivElement | null = null;

	// Event handlers (stored for cleanup)
	#handleMouseDown: ((e: MouseEvent) => void) | null = null;
	#handleMouseMove: ((e: MouseEvent) => void) | null = null;
	#handleMouseUp: ((e: MouseEvent) => void) | null = null;
	#handleClick: ((e: MouseEvent) => void) | null = null;

	constructor (config: RotationGizmoManagerConfig) {
		this.#config = config;

		setupGizmoEventsCfn(this, {});
	}

	// ── Public API ──────────────────────────────────────────────────────────

	/**
	 * Whether a gizmo is currently active on an object
	 */
	get isActive (): boolean {
		return this.#activeObjectId !== null;
	}

	/**
	 * The ID of the object the gizmo is currently active on
	 */
	get activeObjectId (): string | null {
		return this.#activeObjectId;
	}

	/**
	 * Activates the rotation gizmo on the object with the given ID.
	 * Finds the object in the scene by userData.id, computes its bounding box,
	 * and creates a single torus ring centered on it. The ring axis is determined
	 * by the object's userData.subtype (placement).
	 * @param objectId The userData.id of the target appliance
	 */
	activate (objectId: string): void {
		const { scene } = this.#config;

		// If gizmo is already active on this object, do nothing
		if (this.#activeObjectId === objectId) return;

		// Deactivate any existing gizmo first
		if (this.#activeObjectId) {
			this.deactivate();
		}

		// Find the target object in the scene
		let targetObject: Object3D | null = null;
		scene.traverse((child) => {
			if (child.userData?.id === objectId) {
				targetObject = child;
			}
		});

		if (!targetObject) {
			console.warn(`RotationGizmoManager: Object with id "${objectId}" not found in scene`);
			return;
		}

		this.#activeObjectId = objectId;
		this.#targetObject = targetObject;

		// Create gizmo rings around the target
		const gizmoGroup = createGizmoRingsCfn(this, { targetObject: targetObject as Group | Mesh });
		scene.add(gizmoGroup);
	}

	/**
	 * Deactivates the rotation gizmo: removes rings from the scene
	 * and disposes their geometry and materials.
	 */
	deactivate (): void {
		const { scene } = this.#config;

		// Dispose ring geometries and materials
		if (this.#gizmoRings) {
			for (const ring of this.#gizmoRings) {
				const geometry = ring.geometry as TorusGeometry;
				const material = ring.material as MeshBasicMaterial;
				geometry.dispose();
				material.dispose();
			}
			this.#gizmoRings = null;
		}

		// Remove gizmo group from scene
		if (this.#gizmoGroup) {
			scene.remove(this.#gizmoGroup);
			this.#gizmoGroup = null;
		}

		this.#activeObjectId = null;
		this.#targetObject = null;

		// Reset drag state
		this.#isDragging = false;
		this.#dragAxis = null;
		this.#dragStartAngle = null;
		this.#accumulatedAngle = 0;
		this.#planeNormal = null;
		this.#planeCenter = null;
		this.#initialTargetRotation = null;
		this.#initialTargetPosition = null;
		this.#bboxCenter = null;
		this.#ringNormal = null;
		this.#initialWorldPosition = null;
		this.#worldRotAxis = null;
	}

	/**
	 * Dispose of all resources and event listeners
	 */
	dispose (): void {
		disposeCfn(this, {});
	}

	// ── Internal state for cfn access ───────────────────────────────────────

	/** @internal */ get internalConfig (): RotationGizmoManagerConfig {
		return this.#config;
	}

	/** @internal */ get internalActiveObjectId (): string | null {
		return this.#activeObjectId;
	}
	/** @internal */ set internalActiveObjectId (id: string | null) {
		this.#activeObjectId = id;
	}

	/** @internal */ get internalTargetObject (): Object3D | null {
		return this.#targetObject;
	}
	/** @internal */ set internalTargetObject (obj: Object3D | null) {
		this.#targetObject = obj;
	}

	/** @internal */ get internalGizmoGroup (): Group | null {
		return this.#gizmoGroup;
	}
	/** @internal */ set internalGizmoGroup (group: Group | null) {
		this.#gizmoGroup = group;
	}

	/** @internal */ get internalGizmoRings (): Array<Mesh> | null {
		return this.#gizmoRings;
	}
	/** @internal */ set internalGizmoRings (rings: Array<Mesh> | null) {
		this.#gizmoRings = rings;
	}

	/** @internal */ get internalIsDragging (): boolean {
		return this.#isDragging;
	}
	/** @internal */ set internalIsDragging (dragging: boolean) {
		// Track when a drag just finished so the subsequent click doesn't deactivate
		if (this.#isDragging && !dragging) {
			this.#justFinishedDrag = true;
		}
		this.#isDragging = dragging;
	}

	/** @internal */ get internalJustFinishedDrag (): boolean {
		return this.#justFinishedDrag;
	}
	/** @internal */ set internalJustFinishedDrag (value: boolean) {
		this.#justFinishedDrag = value;
	}

	/** @internal */ get internalDragAxis (): 'x' | 'y' | 'z' | null {
		return this.#dragAxis;
	}
	/** @internal */ set internalDragAxis (axis: 'x' | 'y' | 'z' | null) {
		this.#dragAxis = axis;
	}

	/** @internal */ get internalDragStartAngle (): number | null {
		return this.#dragStartAngle;
	}
	/** @internal */ set internalDragStartAngle (angle: number | null) {
		this.#dragStartAngle = angle;
	}

	/** @internal */ get internalAccumulatedAngle (): number {
		return this.#accumulatedAngle;
	}
	/** @internal */ set internalAccumulatedAngle (angle: number) {
		this.#accumulatedAngle = angle;
	}

	/** @internal */ get internalPlaneNormal (): Vector3 | null {
		return this.#planeNormal;
	}
	/** @internal */ set internalPlaneNormal (normal: Vector3 | null) {
		this.#planeNormal = normal;
	}

	/** @internal */ get internalPlaneCenter (): Vector3 | null {
		return this.#planeCenter;
	}
	/** @internal */ set internalPlaneCenter (center: Vector3 | null) {
		this.#planeCenter = center;
	}

	/** @internal */ get internalInitialTargetRotation (): { x: number; y: number; z: number } | null {
		return this.#initialTargetRotation;
	}
	/** @internal */ set internalInitialTargetRotation (rotation: { x: number; y: number; z: number } | null) {
		this.#initialTargetRotation = rotation;
	}

	/** @internal */ get internalInitialTargetPosition (): Vector3 | null {
		return this.#initialTargetPosition;
	}
	/** @internal */ set internalInitialTargetPosition (position: Vector3 | null) {
		this.#initialTargetPosition = position;
	}

	/** @internal */ get internalBboxCenter (): Vector3 | null {
		return this.#bboxCenter;
	}
	/** @internal */ set internalBboxCenter (center: Vector3 | null) {
		this.#bboxCenter = center;
	}

	/** @internal */ get internalRingNormal (): Vector3 | null {
		return this.#ringNormal;
	}
	/** @internal */ set internalRingNormal (normal: Vector3 | null) {
		this.#ringNormal = normal;
	}

	/** @internal */ get internalInitialWorldPosition (): Vector3 | null {
		return this.#initialWorldPosition;
	}
	/** @internal */ set internalInitialWorldPosition (pos: Vector3 | null) {
		this.#initialWorldPosition = pos;
	}

	/** @internal */ get internalWorldRotAxis (): Vector3 | null {
		return this.#worldRotAxis;
	}
	/** @internal */ set internalWorldRotAxis (axis: Vector3 | null) {
		this.#worldRotAxis = axis;
	}

	/** @internal */ get internalHandleMouseDown (): ((e: MouseEvent) => void) | null {
		return this.#handleMouseDown;
	}
	/** @internal */ set internalHandleMouseDown (handler: ((e: MouseEvent) => void) | null) {
		this.#handleMouseDown = handler;
	}

	/** @internal */ get internalHandleMouseMove (): ((e: MouseEvent) => void) | null {
		return this.#handleMouseMove;
	}
	/** @internal */ set internalHandleMouseMove (handler: ((e: MouseEvent) => void) | null) {
		this.#handleMouseMove = handler;
	}

	/** @internal */ get internalHandleMouseUp (): ((e: MouseEvent) => void) | null {
		return this.#handleMouseUp;
	}
	/** @internal */ set internalHandleMouseUp (handler: ((e: MouseEvent) => void) | null) {
		this.#handleMouseUp = handler;
	}

	/** @internal */ get internalHandleClick (): ((e: MouseEvent) => void) | null {
		return this.#handleClick;
	}
	/** @internal */ set internalHandleClick (handler: ((e: MouseEvent) => void) | null) {
		this.#handleClick = handler;
	}

	/** @internal */ get internalAngleLabelElement (): HTMLDivElement | null {
		return this.#angleLabelElement;
	}
	/** @internal */ set internalAngleLabelElement (el: HTMLDivElement | null) {
		this.#angleLabelElement = el;
	}
}
